import cgmIcon from "../../assets/CGM.svg";
import hrIcon from "../../assets/heartrate.svg";
import spo2Icon from "../../assets/SPO2.svg";
import stepsIcon from "../../assets/Steps.svg";
import hrvIcon from "../../assets/HRV.svg";
import stressIcon from "../../assets/Stress.png";

import sgHeartRate from "../../assets/singlegraph/Heart_rate.svg";
import sgSpo2 from "../../assets/singlegraph/Spo2.svg";
import sgSteps from "../../assets/singlegraph/steps.svg";
import sgStress from "../../assets/singlegraph/stress.svg";
import sgCgm from "../../assets/singlegraph/CGM.svg";

import "../../styles/singlegraph.css";
import "../../styles/allgraph.css";
import "../../styles/calendar.css";

import { useState, useRef, useEffect, useMemo, useCallback } from "react";

import HeartRateGraph from "./singlegraphs/heartrate";
import Spo2Graph from "./singlegraphs/spo2";
import HrvGraph from "./singlegraphs/hrv";
import CgmGraph from "./singlegraphs/cgm";
import StressGraph from "./singlegraphs/stress";
import StepsGraph from "./singlegraphs/steps";
import SleepGraph from "./singlegraphs/sleep";

// ─── Constants ────────────────────────────────────────────────────────────────
const FIVE_MIN_MS = 5 * 60 * 1000;
const BUCKETS_PER_DAY = 288;
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const S3_BASE = process.env.REACT_APP_S3_BASE_URL || "https://neosynkserver.s3.ap-south-1.amazonaws.com";

// ─── Time helpers ─────────────────────────────────────────────────────────────
function toIST(ms) { return new Date(ms + IST_OFFSET_MS); }
function msToISTDateKey(ms) {
  const d = toIST(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
function toMs(ts) { if (!ts) return 0; return ts < 1e12 ? ts * 1000 : ts; }
function itemDateKey(item) { return msToISTDateKey(toMs(item.ts)); }
function anchorToISTKey(anchor) {
  const d = new Date(anchor);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// h:mm, no leading zero on the hour (e.g. "9:30", "11:25")
function formatTime(ts) {
  const d = new Date(ts);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Calendar se pick ki gayi date ko arrows (◀ ▶) jaisa behave karwane ke liye:
// din naya, time-of-day purana (aaj ho toh abhi ka time).
// Pehle calendar 00:00 wali date bhej raha tha, jisse parent ka fetch window
// shift ho jaata tha aur data half aata ya aata hi nahi tha.
function applyTimeOfDay(picked, reference) {
  const now = new Date();
  if (picked.toDateString() === now.toDateString()) return now;

  const d = new Date(picked);
  d.setHours(
    reference.getHours(),
    reference.getMinutes(),
    reference.getSeconds(),
    reference.getMilliseconds()
  );
  return d;
}

// ─── Sleep-stage helpers ──────────────────────────────────────────────────────
function normaliseStage(raw) {
  if (!raw) return "light_sleep";
  switch (raw) {
    case "rem": return "rem_sleep";
    case "awake": return "awake";
    case "non_sleep": return "awake";
    case "unknown": return "light_sleep";
    default: return raw;
  }
}

const SOURCE_PRIORITY = { jcband: 0, jcring: 0, qring: 1 };

function pickBestSource(items) {
  if (!items?.length) return [];
  const bySource = {};
  items.forEach(item => {
    const src = (item.source || "unknown").toLowerCase();
    if (!bySource[src]) bySource[src] = [];
    bySource[src].push(item);
  });
  const sources = Object.keys(bySource);
  if (sources.length === 1) return items;
  sources.sort((a, b) => (SOURCE_PRIORITY[a] ?? 99) - (SOURCE_PRIORITY[b] ?? 99));
  return bySource[sources[0]];
}

function buildSegments(graph) {
  if (!graph?.length) return [];
  const sorted = graph
    .map(s => ({ stage: normaliseStage(s.stage), startMs: toMs(s.start), endMs: toMs(s.end) }))
    .filter(s => s.startMs > 0 && s.endMs > s.startMs)
    .sort((a, b) => a.startMs - b.startMs);
  if (!sorted.length) return [];
  const segments = [];
  let current = { ...sorted[0] };
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const next = sorted[i];
    if (next.startMs - prev.endMs > 60_000) { segments.push(current); current = { ...next }; continue; }
    if (next.stage === current.stage) { current.endMs = next.endMs; }
    else { segments.push(current); current = { ...next }; }
  }
  segments.push(current);
  return segments;
}

// ─── build5MinBuckets ─────────────────────────────────────────────────────────
function build5MinBuckets(points, selectedDate) {
  const start = new Date(selectedDate);
  start.setHours(0, 0, 0, 0);
  const startTs = start.getTime();

  const bucketValues = new Float64Array(BUCKETS_PER_DAY).fill(NaN);
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (p.value == null) continue;
    const index = Math.floor((p.ts - startTs) / FIVE_MIN_MS);
    if (index >= 0 && index < BUCKETS_PER_DAY) {
      bucketValues[index] = p.value;
    }
  }

  const result = new Array(BUCKETS_PER_DAY);
  for (let i = 0; i < BUCKETS_PER_DAY; i++) {
    result[i] = {
      time: startTs + i * FIVE_MIN_MS,
      value: isNaN(bucketValues[i]) ? null : bucketValues[i],
    };
  }
  return result;
}

// ─── Food-log helpers ───────────────────────────────────────────────────────
// avg() reads a {min,max} range object (used across calories/carbs/etc.)
function avg(obj) { return obj ? Math.round((obj.min + obj.max) / 2) : null; }

// Meal type — prefers an explicit field from the API if the payload has one
// (checked under a few likely key names), otherwise falls back to inferring
// from the hour of day. Adjust the explicit-field keys below if your API
// uses a different name than meal_type / mealType / meal.
function getMealType(event) {
  const explicit = (event.raw?.meal_type || event.raw?.mealType || event.raw?.meal || "")
    .toString()
    .toLowerCase();
  if (explicit.includes("break")) return "Breakfast";
  if (explicit.includes("lunch")) return "Lunch";
  if (explicit.includes("snack")) return "Snack";
  if (explicit.includes("dinner")) return "Dinner";

  const hour = new Date(event.ts).getHours();
  if (hour < 11) return "Breakfast";
  if (hour < 15) return "Lunch";
  if (hour < 18) return "Snack";
  return "Dinner";
}

function getMealIcon(mealType) { return mealType === "Dinner" ? "🌙" : "☀️"; }

// Glucose impact — based on the actual CGM value around the meal, not carbs.
// Looks at the peak glucose reading in the 2 hours after the meal (the
// standard postprandial window); falls back to the nearest available
// reading if nothing falls inside that window. Bands (mg/dL) per Dr. Ankit:
//   < 140        → Normal
//   140 – 179    → Mild
//   180 – 220    → Moderate
//   ≥ 220        → High
const POST_MEAL_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours

function getGlucoseImpact(event, cgmPoints) {
  const explicit = event.raw?.glucose_impact || event.raw?.glycemic_impact || event.raw?.impact;
  if (explicit) return explicit;

  if (!cgmPoints?.length) return null;

  const windowEnd = event.ts + POST_MEAL_WINDOW_MS;
  const windowPoints = cgmPoints.filter(p => p.value != null && p.ts >= event.ts && p.ts <= windowEnd);

  let peak = null;
  if (windowPoints.length) {
    peak = windowPoints.reduce((max, p) => (p.value > max ? p.value : max), -Infinity);
  } else {
    // No reading fell inside the post-meal window — use whichever CGM
    // point is closest in time to the meal instead.
    let nearestVal = null, minDiff = Infinity;
    cgmPoints.forEach(p => {
      if (p.value == null) return;
      const diff = Math.abs(p.ts - event.ts);
      if (diff < minDiff) { minDiff = diff; nearestVal = p.value; }
    });
    peak = nearestVal;
  }

  if (peak == null || peak === -Infinity) return null;
  if (peak < 140) return "Normal";
  if (peak < 180) return "Mild";
  if (peak <= 220) return "Moderate";
  return "High";
}

function impactColor(impact) {
  switch ((impact || "").toLowerCase()) {
    case "normal": return "#16a34a";
    case "mild": return "#65a30d";
    case "moderate": return "#d97706";
    case "high": return "#dc2626";
    default: return "#64748b";
  }
}

// ─── CaloriePopup ─────────────────────────────────────────────────────────────
function CaloriePopup({ event, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const cleanKey = event.image_url ? event.image_url.replace(/^\/+/, "") : null;
  const imgSrc = cleanKey ? `${S3_BASE}/${cleanKey}` : null;
  const isLogged = event.raw?.is_logged === true;

  const nutrients = [
    { label: "Calories", value: avg(event.raw.calories), unit: "kcal", color: "#ef4444" },
    { label: "Carbs", value: avg(event.raw.carbs), unit: "g", color: "#f59e0b" },
    { label: "Protein", value: avg(event.raw.proteins), unit: "g", color: "#3b82f6" },
    { label: "Fat", value: avg(event.raw.fat), unit: "g", color: "#8b5cf6" },
    { label: "Fiber", value: avg(event.raw.fiber), unit: "g", color: "#22c55e" },
    { label: "Weight", value: avg(event.raw.weight), unit: "g", color: "#64748b" },
  ].filter(n => n.value != null);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(2px)"
      }}
    >
      <div
        ref={ref}
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          width: 340,
          maxWidth: "90vw",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          fontFamily: "inherit"
        }}
      >
        <div style={{ position: "relative", width: "100%", height: 220, background: "#f1f5f9" }}>
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={event.dish_name}
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#f1f5f9" }}
              onError={e => { e.target.style.display = "none"; }}
            />
          ) : (
            <div style={{
              width: "100%", height: "100%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 48
            }}>🍽️</div>
          )}

          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "none",
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              fontSize: 14,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1
            }}
          >✕</button>

          <div style={{
            position: "absolute",
            top: 10,
            left: 10,
            background: isLogged ? "rgba(22,163,74,0.9)" : "rgba(100,116,139,0.9)",
            color: "#fff",
            borderRadius: 20,
            padding: "3px 10px",
            fontSize: 11,
            fontWeight: 700
          }}>
            {isLogged ? "✓ Logged" : "Not logged"}
          </div>

          {event.raw.confidence != null && (
            <div style={{
              position: "absolute",
              bottom: 10,
              right: 10,
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              borderRadius: 20,
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600
            }}>
              {event.raw.confidence}% confidence
            </div>
          )}
        </div>

        <div style={{ padding: "16px 18px 20px" }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", marginBottom: 3 }}>
              {event.dish_name}
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {new Date(event.ts).toLocaleTimeString("en-IN", {
                hour: "2-digit", minute: "2-digit", hour12: true
              })}
            </div>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
            marginBottom: 14
          }}>
            {nutrients.map(n => (
              <div key={n.label} style={{
                background: "#f8fafc",
                borderRadius: 10,
                padding: "8px 6px",
                textAlign: "center",
                border: `1.5px solid ${n.color}22`
              }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: n.color }}>
                  {n.value}
                  <span style={{ fontSize: 10, fontWeight: 400, marginLeft: 1 }}>{n.unit}</span>
                </div>
                <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{n.label}</div>
              </div>
            ))}
          </div>

          {event.raw.note && (
            <div style={{
              fontSize: 11,
              color: "#64748b",
              background: "#f8fafc",
              borderRadius: 8,
              padding: "8px 10px",
              lineHeight: 1.5,
              maxHeight: 72,
              overflowY: "auto"
            }}>
              {event.raw.note}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── FoodLogCard ──────────────────────────────────────────────────────────────
// A single meal card shown in the strip below the graph. Clicking it opens
// the CaloriePopup with full nutrient detail. Shows the full (uncropped)
// dish photo up top, plus an is_logged badge so it's clear whether this
// entry was actually logged by the user or just detected.
function FoodLogCard({ event, cgmPoints, onOpen }) {
  const [imgFailed, setImgFailed] = useState(false);

  const cleanKey = event.image_url ? event.image_url.replace(/^\/+/, "") : null;
  const imgSrc = cleanKey ? `${S3_BASE}/${cleanKey}` : null;
  const mealType = getMealType(event);
  const mealIcon = getMealIcon(mealType);
  const carbs = avg(event.raw?.carbs);
  const impact = getGlucoseImpact(event, cgmPoints);
  const isLogged = event.raw?.is_logged === true;

  return (
    <div
      onClick={() => onOpen(event)}
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 160,
        maxWidth: 180,
        flex: "0 0 auto",
        background: "#fff",
        border: "1.5px solid #f5c451",
        borderRadius: 12,
        overflow: "hidden",
        cursor: "pointer",
        transition: "box-shadow 0.15s, transform 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
    >
      <div style={{ position: "relative", width: "100%", height: 110, background: "#f1f5f9" }}>
        {imgSrc && !imgFailed ? (
          <img
            src={imgSrc}
            alt={event.dish_name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30 }}>🍽️</div>
        )}

        <span style={{
          position: "absolute",
          top: 6,
          left: 6,
          background: isLogged ? "#16a34a" : "#94a3b8",
          color: "#fff",
          fontSize: 10,
          fontWeight: 700,
          borderRadius: 20,
          padding: "2px 8px",
        }}>
          {isLogged ? "✓ Logged" : "Not logged"}
        </span>

        <span style={{
          position: "absolute",
          bottom: 6,
          right: 6,
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          fontSize: 11,
          borderRadius: 20,
          padding: "2px 8px",
        }}>
          {mealIcon} {new Date(event.ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
        </span>
      </div>

      <div style={{ padding: "8px 12px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{mealType}</div>

        <div style={{ fontSize: 11.5, color: "#475569", lineHeight: 1.4 }}>
          {event.dish_name}
        </div>

        {carbs != null && (
          <div style={{ fontSize: 11, color: "#64748b" }}>Carbs {carbs}g</div>
        )}

        {impact && (
          <div style={{ fontSize: 11.5, fontWeight: 700, color: impactColor(impact) }}>
            Glucose Impact: {impact}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FoodLogStrip ─────────────────────────────────────────────────────────────
// Horizontal row of FoodLogCards shown below the graph, in chronological order.
function FoodLogStrip({ events, cgmPoints, onOpen }) {
  if (!events?.length) return null;
  const sorted = [...events].sort((a, b) => a.ts - b.ts);

  return (
    <div style={{
      display: "flex",
      gap: 12,
      overflowX: "auto",
      padding: "14px 4px 4px",
      marginTop: 4,
    }}>
      {sorted.map((evt, i) => (
        <FoodLogCard key={i} event={evt} cgmPoints={cgmPoints} onOpen={onOpen} />
      ))}
    </div>
  );
}

// ─── MiniCalendar ─────────────────────────────────────────────────────────────
function MiniCalendar({ selectedDate, onSelect, onClose }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    const now = new Date();
    if (viewYear > now.getFullYear() || (viewYear === now.getFullYear() && viewMonth >= now.getMonth())) return;
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1);
  }

  const monthName = new Date(viewYear, viewMonth, 1).toLocaleString("en-IN", { month: "long" });
  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="mini-calendar" ref={ref}>
      <div className="mc-header">
        <button className="mc-nav" onClick={prevMonth}>‹</button>
        <span className="mc-title">{monthName} {viewYear}</span>
        <button className="mc-nav" onClick={nextMonth}>›</button>
      </div>
      <div className="mc-grid-header">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => <span key={d} className="mc-day-name">{d}</span>)}
      </div>
      <div className="mc-grid">
        {cells.map((day, i) => {
          if (!day) return <span key={`empty-${i}`} />;
          const cellDate = new Date(viewYear, viewMonth, day);
          const isToday = cellDate.toDateString() === today.toDateString();
          const isSelected = cellDate.toDateString() === selectedDate.toDateString();
          const isFuture = cellDate > today;
          return (
            <button
              key={day}
              className={["mc-day", isToday ? "mc-today" : "", isSelected ? "mc-selected" : "", isFuture ? "mc-disabled" : ""].join(" ")}
              disabled={isFuture}
              onClick={() => { onSelect(cellDate); onClose(); }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Day data-log popup ───────────────────────────────────────────────────
// Small scrollable panel listing the raw readings for whichever graph is
// currently selected (not every metric) — most recent reading first.
function DayDataPanel({ metricLabel, unit, readings, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{
        background: "#fff",
        borderRadius: 12,
        width: 220,
        maxHeight: 320,
        overflowY: "auto",
        boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
        padding: "10px 0",
      }}
    >
      <div style={{
        padding: "0 14px 8px",
        fontSize: 13,
        fontWeight: 700,
        color: "#0f172a",
        borderBottom: "1px solid #f1f5f9"
      }}>
        {metricLabel} readings
      </div>

      {readings.length > 0 ? (
        <div style={{ padding: "4px 14px" }}>
          {readings.map((r, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "6px 0",
                fontSize: 12.5,
                color: "#334155",
                borderBottom: "1px solid #f8fafc"
              }}
            >
              <span>{r.time}</span>
              <span style={{ fontWeight: 600, color: "#0f172a" }}>
                {r.value}{unit ? ` ${unit}` : ""}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ padding: "10px 14px", fontSize: 12, color: "#94a3b8" }}>
          No data recorded for this day.
        </div>
      )}
    </div>
  );
}

// Small note-icon button that opens the DayDataPanel.
function NoteIconButton({ onClick }) {
  return (
    <button
      className="date-arrow"
      onClick={onClick}
      title="Data log for this day"
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M14 3v6h5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M8.5 13h7M8.5 16.5h7M8.5 9.5h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </button>
  );
}

// ─── Metric nav config ──────────────────────────────────────────────────────
const METRICS = [
  { key: "HR", label: "Heart Rate", icon: hrIcon, color: "#ef4444" },
  { key: "SPO2", label: "Spo2", icon: spo2Icon, color: "#8b5cf6" },
  { key: "HRV", label: "HRV", icon: hrvIcon, color: "#f59e0b" },
  { key: "CGM", label: "CGM(Glucose)", icon: cgmIcon, color: "#3b82f6" },
  { key: "Stress", label: "Stress", icon: stressIcon, color: "#ec4899" },
  { key: "Steps", label: "Steps", icon: stepsIcon, color: "#22c55e" },
  { key: "Sleep", label: "Sleep", icon: null, color: "#6366f1" },
];

const NAV_ACTIVE_COLOR = "#6C5CE7";

function MetricNavButton({ metric, active, onClick }) {
  return (
    <button
      className={`metric-nav-pill${active ? " metric-nav-pill-active" : ""}`}
      onClick={onClick}
      style={active ? { background: NAV_ACTIVE_COLOR, borderColor: NAV_ACTIVE_COLOR } : undefined}
    >
      {metric.key === "Sleep" ? (
        <span style={{ fontSize: 14, lineHeight: 1 }}>🌙</span>
      ) : (
        <img
          src={metric.icon}
          alt=""
          className="metric-nav-pill-icon"
          style={{ filter: active ? "brightness(0) invert(1)" : "none", opacity: active ? 1 : 0.75 }}
        />
      )}
      {metric.label}
    </button>
  );
}

// ─── Metric summary bar ──────────────────────────────────────────────────────
const SUMMARY_ICONS = {
  HR: sgHeartRate,
  SPO2: sgSpo2,
  HRV: sgHeartRate,
  CGM: sgCgm,
  Stress: sgStress,
  Steps: sgSteps,
};

function getSummaryStatus(metricKey, value) {
  if (value == null || Number.isNaN(value)) return { label: "--", tone: "neutral" };
  switch (metricKey) {
    case "HR":
      if (value < 60) return { label: "Low", tone: "warn" };
      if (value > 100) return { label: "High", tone: "bad" };
      return { label: "Normal", tone: "good" };
    case "SPO2":
      if (value >= 95) return { label: "Excellent", tone: "good" };
      if (value >= 90) return { label: "Good", tone: "warn" };
      return { label: "Low", tone: "bad" };
    case "HRV":
      if (value >= 50) return { label: "Good", tone: "good" };
      if (value >= 30) return { label: "Fair", tone: "warn" };
      return { label: "Low", tone: "bad" };
    case "CGM":
      if (value < 70) return { label: "Low", tone: "bad" };
      if (value > 140) return { label: "High", tone: "bad" };
      return { label: "In-range", tone: "good" };
    case "Stress":
      if (value < 40) return { label: "Low", tone: "good" };
      if (value < 70) return { label: "Moderate", tone: "warn" };
      return { label: "High", tone: "bad" };
    case "Steps":
      return value >= 5000 ? { label: "On-track", tone: "good" } : { label: "Behind", tone: "warn" };
    default:
      return { label: "--", tone: "neutral" };
  }
}

const TONE_STYLES = {
  good: { color: "#16a34a", bg: "#dcfce7" },
  warn: { color: "#d97706", bg: "#fef3c7" },
  bad: { color: "#dc2626", bg: "#fee2e2" },
  neutral: { color: "#6b7280", bg: "#f1f5f9" },
};

function StatusChip({ tone, label }) {
  const s = TONE_STYLES[tone] || TONE_STYLES.neutral;
  return (
    <span className="status-chip" style={{ color: s.color, background: s.bg }}>
      {label}
    </span>
  );
}

function lastReading(points) {
  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i].value != null) return points[i];
  }
  return null;
}

function MetricSummaryBar({ hrPoints, spo2Points, hrvPoints, cgmPoints, stressPoints, stepsPoints }) {
  const hr = lastReading(hrPoints);
  const spo2 = lastReading(spo2Points);
  const hrv = lastReading(hrvPoints);
  const cgm = lastReading(cgmPoints);
  const stress = lastReading(stressPoints);
  const totalSteps = stepsPoints.reduce((sum, p) => sum + (p.value || 0), 0);

  const cards = [
    { key: "HR", label: "Heart Rate", value: hr?.value != null ? Math.round(hr.value) : null, unit: "bpm" },
    { key: "SPO2", label: "Spo2", value: spo2?.value != null ? Math.round(spo2.value) : null, unit: "%" },
    { key: "HRV", label: "HRV", value: hrv?.value != null ? Math.round(hrv.value) : null, unit: "ms" },
    { key: "CGM", label: "CGM", value: cgm?.value != null ? Math.round(cgm.value) : null, unit: "mg/dL" },
    { key: "Steps", label: "Steps", value: totalSteps || null, unit: "" },
    { key: "Stress", label: "Stress", value: stress?.value != null ? Math.round(stress.value) : null, unit: "" },
  ];

  return (
    <div className="metric-summary-bar">
      {cards.map(c => {
        const status = getSummaryStatus(c.key, c.value);
        return (
          <div className="metric-summary-card" key={c.key}>
            <div className="metric-summary-top">
              <span className="metric-summary-label">{c.label}</span>
              <img src={SUMMARY_ICONS[c.key]} alt="" className="metric-summary-icon" />
            </div>
            <div className="metric-summary-value">
              {c.value != null ? c.value.toLocaleString("en-IN") : "--"}
              {c.unit && <span className="metric-summary-unit"> {c.unit}</span>}
            </div>
            <StatusChip tone={status.tone} label={status.label} />
          </div>
        );
      })}
    </div>
  );
}

// ─── SingleGraph ────────────────────────────────────────────────────────────
export default function SingleGraph({ trends, selectedDate, onDateChange }) {
  const [selectedMetric, setSelectedMetric] = useState("HR");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [activeCalorie, setActiveCalorie] = useState(null);

  const wearable = useMemo(
    () => trends?.data?.result?.wearable_trends?.data || [],
    [trends]
  );

  const { hasJC, hasQRing } = useMemo(() => {
    const sources = new Set(wearable.map(x => x.source).filter(Boolean));
    return { hasJC: sources.has("jcband"), hasQRing: sources.has("qring") };
  }, [wearable]);

  const [selectedSource, setSelectedSource] = useState(
    hasJC ? "jcband" : hasQRing ? "qring" : null
  );

  // useState ka initial value sirf pehle render pe chalta hai. Agar tab data
  // nahi tha (ya naye din ka data baad mein aaya), toh source null reh jaata
  // tha aur saare graphs khaali dikhte the. Isliye data ke saath sync rakho.
  useEffect(() => {
    const valid =
      (selectedSource === "jcband" && hasJC) ||
      (selectedSource === "qring" && hasQRing);
    if (!valid) {
      setSelectedSource(hasJC ? "jcband" : hasQRing ? "qring" : null);
    }
  }, [hasJC, hasQRing, selectedSource]);

  const hrPoints = useMemo(() => wearable
    .filter(x => x.type === "heart_rate" && x.source === selectedSource)
    .flatMap(x => x.heart_rate?.vital_graph || [])
    .map(v => ({ ts: new Date(v.ts).getTime(), value: v.value === 0 ? null : v.value })),
    [wearable, selectedSource]);

  const spo2Points = useMemo(() => wearable
    .filter(x => x.type === "spo2" && x.source === selectedSource)
    .flatMap(x => x.spo2?.vital_graph || [])
    .map(v => ({ ts: new Date(v.ts).getTime(), value: v.value === 0 ? null : v.value })),
    [wearable, selectedSource]);

  const stepsPoints = useMemo(() => wearable
    .filter(x => x.type === "activity" && x.source === selectedSource)
    .flatMap(x => x.activity?.step_graph || [])
    .map(v => ({ ts: new Date(v.ts).getTime(), value: v.steps })),
    [wearable, selectedSource]);

  const hrvPoints = useMemo(() => wearable
    .filter(x => x.type === "hrv" && x.source === selectedSource)
    .flatMap(x => x.hrv?.vital_graph || [])
    .map(v => ({ ts: new Date(v.ts).getTime(), value: v.value === 0 ? null : v.value })),
    [wearable, selectedSource]);

  const stressPoints = useMemo(() => wearable
    .filter(x => x.type === "stress" && x.source === selectedSource)
    .flatMap(x => x.stress?.vital_graph || [])
    .map(v => ({ ts: new Date(v.ts).getTime(), value: v.value === 0 ? null : v.value })),
    [wearable, selectedSource]);

  const cgmPoints = useMemo(() => {
    const raw = trends?.data?.result?.cgm_trends || [];
    return raw.map(v => ({ ts: v.ts < 1e12 ? v.ts * 1000 : v.ts, value: v.value }));
  }, [trends]);

  // ── Calorie / food log events ─────────────────────────────────────────────
  // Use the item's own timestamp directly — the parent fetches calorie_history
  // for the selected date, so all items already belong to that day.
  const calorieEvents = useMemo(() => {
    const items = trends?.data?.result?.calorie_history?.items || [];
    const selectedKey = anchorToISTKey(selectedDate);
    return items
      .filter(item => item.image_url || item.dish_name)
      .map(item => ({
        ts: new Date(item.timestamp).getTime(),
        image_url: item.image_url || null,
        dish_name: item.dish_name || "Food",
        calories: item.calories || null,
        raw: item
      }))
      // only keep meals that actually fall on the day currently selected
      .filter(evt => itemDateKey(evt) === selectedKey);
  }, [trends, selectedDate]);

  const hrData = useMemo(() => build5MinBuckets(hrPoints, selectedDate), [hrPoints, selectedDate]);
  const spo2Data = useMemo(() => build5MinBuckets(spo2Points, selectedDate), [spo2Points, selectedDate]);
  const hrvData = useMemo(() => build5MinBuckets(hrvPoints, selectedDate), [hrvPoints, selectedDate]);
  const cgmData = useMemo(() => build5MinBuckets(cgmPoints, selectedDate), [cgmPoints, selectedDate]);
  const stressData = useMemo(() => build5MinBuckets(stressPoints, selectedDate), [stressPoints, selectedDate]);
  const stepsData = useMemo(() => build5MinBuckets(stepsPoints, selectedDate), [stepsPoints, selectedDate]);

  // ── Sleep pipeline (only used when "Sleep" is selected, but also feeds the
  // Day/Night toggle on every other graph) ──────────────────────────────────
  const sleepItemsByDate = useMemo(() => {
    const raw = {};
    wearable.forEach(item => {
      if (item.type !== "sleep" || !item.sleep?.sleep_graph?.length) return;
      const key = itemDateKey(item);
      if (!raw[key]) raw[key] = [];
      raw[key].push(item);
    });
    const map = {};
    Object.entries(raw).forEach(([date, items]) => { map[date] = pickBestSource(items); });
    return map;
  }, [wearable]);

  const selectedDateKey = useMemo(() => anchorToISTKey(selectedDate), [selectedDate]);
  const selectedMidnight = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [selectedDate]);

  const sleepDayData = useMemo(() => {
    const prevDate = new Date(selectedDate);
    prevDate.setDate(prevDate.getDate() - 1);
    const prevKey = anchorToISTKey(prevDate);

    const prevItems = sleepItemsByDate[prevKey] || [];
    let prevSegs = [];
    pickBestSource(prevItems).forEach(item => {
      const sl = item.sleep;
      if (!sl?.sleep_graph?.length) return;
      const segs = buildSegments(sl.sleep_graph).filter(s => s.endMs > selectedMidnight);
      prevSegs = prevSegs.concat(segs);
    });

    const todayItems = pickBestSource(sleepItemsByDate[selectedDateKey] || []);
    if (!todayItems.length && !prevSegs.length) return null;

    let allSegs = [...prevSegs];
    let totalSec = 0, deepSec = 0, shallowSec = 0, rapidSec = 0, awakeSec = 0;
    todayItems.forEach(item => {
      const sl = item.sleep;
      totalSec += sl.total_sleep_duration || 0;
      deepSec += sl.deep_duration || 0;
      shallowSec += sl.shallow_duration || 0;
      rapidSec += sl.rapid_duration || 0;
      awakeSec += sl.awake_duration || 0;
      allSegs = allSegs.concat(buildSegments(sl.sleep_graph));
    });

    if (!allSegs.length) return null;
    allSegs.sort((a, b) => a.startMs - b.startMs);

    return {
      segments: allSegs,
      sleepStart: allSegs[0].startMs,
      sleepEnd: allSegs[allSegs.length - 1].endMs,
      stats: { totalSec, deepSec, shallowSec, rapidSec, awakeSec },
    };
  }, [sleepItemsByDate, selectedDateKey, selectedMidnight, selectedDate]);

  // ── Readings list for the note-icon popup — only the currently selected
  // graph's data, most recent reading first ────────────────────────────────
  const METRIC_LABELS = { HR: "Heart Rate", SPO2: "SpO2", HRV: "HRV", CGM: "CGM", Stress: "Stress", Steps: "Steps", Sleep: "Sleep" };
  const METRIC_UNITS = { HR: "bpm", SPO2: "%", HRV: "ms", CGM: "mg/dL", Stress: "", Steps: "" };

  const selectedReadings = useMemo(() => {
    if (selectedMetric === "Sleep") {
      const segs = sleepDayData?.segments || [];
      return [...segs]
        .sort((a, b) => b.startMs - a.startMs)
        .map(s => ({
          time: `${formatTime(s.startMs)}-${formatTime(s.endMs)}`,
          value: s.stage.replace("_", " "),
        }));
    }

    const bucketMap = { HR: hrData, SPO2: spo2Data, HRV: hrvData, CGM: cgmData, Stress: stressData, Steps: stepsData };
    const data = bucketMap[selectedMetric] || [];
    return data
      .filter(p => p.value != null)
      .sort((a, b) => b.time - a.time)
      .map(p => ({
        time: formatTime(p.time),
        value: Number.isInteger(p.value) ? p.value : Math.round(p.value * 10) / 10,
      }));
  }, [selectedMetric, hrData, spo2Data, hrvData, cgmData, stressData, stepsData, sleepDayData]);

  // ── Date navigation ────────────────────────────────────────────────────
  const goToPreviousDay = useCallback(() => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    onDateChange(d);
  }, [selectedDate, onDateChange]);

  const goToNextDay = useCallback(() => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    if (d <= new Date()) onDateChange(d);
  }, [selectedDate, onDateChange]);

  // FIX: calendar se bhi wahi time-of-day jaisa arrows mein hota hai,
  // warna 00:00 jaane se parent ka fetch window shift ho jaata tha.
  const handleCalendarSelect = useCallback((date) => {
    onDateChange(applyTimeOfDay(date, selectedDate));
  }, [selectedDate, onDateChange]);

  const handleCalendarClose = useCallback(() => setCalendarOpen(false), []);
  const toggleCalendar = useCallback(() => setCalendarOpen(o => !o), []);
  const closeNotes = useCallback(() => setNotesOpen(false), []);
  const toggleNotes = useCallback(() => setNotesOpen(o => !o), []);

  // ── Early exit — MUST come after every hook call ──────────────────────────
  if (!trends) return null;

  const dateControl = (
    <div className="date-nav-wrapper" style={{ display: "flex", alignItems: "center", gap: "10px", position: "relative" }}>
      <button className="date-arrow" onClick={goToPreviousDay}>◀</button>

      {/* Calendar pill + its dropdown live in their own relative wrapper,
          scoped tightly so the dropdown centers under just the pill —
          the data-log (note) button sits fully outside this wrapper. */}
      <div style={{ position: "relative" }}>
        <div
          className="date-pill"
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={toggleCalendar}
        >
          {selectedDate.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
          <span style={{ marginLeft: 5, fontSize: 10, opacity: 0.6 }}>▾</span>
        </div>

        {calendarOpen && (
          <div style={{ position: "absolute", top: "110%", left: "50%", transform: "translateX(-50%)", zIndex: 100 }}>
            <MiniCalendar
              selectedDate={selectedDate}
              onSelect={handleCalendarSelect}
              onClose={handleCalendarClose}
            />
          </div>
        )}
      </div>

      {/* ── Data-log button — outside the calendar wrapper ── */}
      <div style={{ position: "relative" }}>
        <NoteIconButton onClick={toggleNotes} />
        {notesOpen && (
          <div style={{ position: "absolute", top: "110%", left: "50%", transform: "translateX(-50%)", zIndex: 100 }}>
            <DayDataPanel
              metricLabel={METRIC_LABELS[selectedMetric]}
              unit={METRIC_UNITS[selectedMetric]}
              readings={selectedReadings}
              onClose={closeNotes}
            />
          </div>
        )}
      </div>

      {hasJC && hasQRing && (
        <div className="source-toggle">
          <button className={selectedSource === "jcband" ? "active" : ""} onClick={() => setSelectedSource("jcband")}>JC Band</button>
          <button className={selectedSource === "qring" ? "active" : ""} onClick={() => setSelectedSource("qring")}>Q Ring</button>
        </div>
      )}

      <button className="date-arrow" onClick={goToNextDay}>▶</button>
    </div>
  );

  return (
    <div className="cc-single-graph-root">
      {/* ── 1. Metric summary bar ── */}
      <MetricSummaryBar
        hrPoints={hrPoints}
        spo2Points={spo2Points}
        hrvPoints={hrvPoints}
        cgmPoints={cgmPoints}
        stressPoints={stressPoints}
        stepsPoints={stepsPoints}
      />

      {/* ── 2. Metric nav bar ── */}
      <div className="metric-nav-bar">
        {METRICS.map(m => (
          <MetricNavButton
            key={m.key}
            metric={m}
            active={selectedMetric === m.key}
            onClick={() => setSelectedMetric(m.key)}
          />
        ))}
      </div>

      {/* ── 3. Selected graph — routed to its own standalone component ── */}
      {selectedMetric === "HR" && (
        <HeartRateGraph bucketData={hrData} selectedDate={selectedDate} sleepDayData={sleepDayData} dateControl={dateControl} />
      )}
      {selectedMetric === "SPO2" && (
        <Spo2Graph bucketData={spo2Data} selectedDate={selectedDate} sleepDayData={sleepDayData} dateControl={dateControl} />
      )}
      {selectedMetric === "HRV" && (
        <HrvGraph bucketData={hrvData} selectedDate={selectedDate} sleepDayData={sleepDayData} dateControl={dateControl} />
      )}
      {selectedMetric === "CGM" && (
        <CgmGraph bucketData={cgmData} selectedDate={selectedDate} sleepDayData={sleepDayData} dateControl={dateControl} />
      )}
      {selectedMetric === "Stress" && (
        <StressGraph bucketData={stressData} selectedDate={selectedDate} sleepDayData={sleepDayData} dateControl={dateControl} />
      )}
      {selectedMetric === "Steps" && (
        <StepsGraph bucketData={stepsData} selectedDate={selectedDate} dateControl={dateControl} />
      )}
      {selectedMetric === "Sleep" && (
        <SleepGraph sleepDayData={sleepDayData} dateControl={dateControl} />
      )}

      {/* ── 4. Food log strip — shown below every graph except Sleep.
          Clicking a card opens the full-detail CaloriePopup. ── */}
      {selectedMetric !== "Sleep" && (
        <FoodLogStrip events={calorieEvents} cgmPoints={cgmPoints} onOpen={setActiveCalorie} />
      )}

      {activeCalorie && (
        <CaloriePopup
          event={activeCalorie}
          onClose={() => setActiveCalorie(null)}
        />
      )}
    </div>
  );
}